/**
 * SessionCard component
 * Displays a single session item with passage preview, status badge, and timestamp
 * Active cards show progress bar and Continue CTA
 * Clickable to navigate to session detail view
 *
 * MODE TRANSPARENCY AUDIT (US-005 T3): 2026-02-11
 * Verified: This component does NOT reference session.mode field.
 * It displays MCP and serverless sessions identically based only on:
 * - passage (preview text)
 * - status (Active/Completed badge)
 * - updatedAt (timestamp)
 * No mode indicator is visible to students.
 *
 * Modified US-018 T4: Added StarRating display for completed sessions.
 * DD-B: StarRating reads session.starRating only — never recomputed in the frontend.
 * CCM-03: No price/coin/financial data is rendered on any student-facing surface.
 *
 * Modified TS-007 (T11, SR6, AC-TS007.6b): added a genuinely NEW status branch for
 *   status === 'ended_by_parent' (there is no existing cancelled-specific branch to
 *   clone here — badgeText was previously a binary Active/Completed). Renders the
 *   'Ended early' badge with NO star rating and NO Continue/View Report actions;
 *   the Card already has no onClick/role=button for any non-active status, so this
 *   branch is non-tappable exactly like the badge/star suppression requires.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ProgressBar } from '@/components/ProgressBar';
import { StarRating } from '@/components/StarRating';
import { cn } from '@/lib/utils';
import { formatRelativeTime, truncateText } from '@/lib/formatters';
import type { Session } from '@/types/session';
import { getReportUrl } from '@/services/reports';

export interface SessionCardProps {
  /** Session data to display */
  session: Session;
}

/**
 * SessionCard - Displays session preview with click navigation
 * ThinkOtter design: active cards have orange CTA and progress, completed cards are muted
 */
export function SessionCard({ session }: SessionCardProps) {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/session/${session.sessionId}`);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  const handleContinueClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent double navigation from card click
    handleClick();
  };

  const [isLoadingReport, setIsLoadingReport] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);

  const handleViewReport = async (e: React.MouseEvent) => {
    // BUG-20260616-202933: Open tab SYNCHRONOUSLY inside the user-gesture to defeat
    // popup blocker — window.open after an await forfeits gesture context - 2026-06-16
    e.stopPropagation(); // Prevent card click navigation
    setIsLoadingReport(true);
    setReportError(null);

    // Step 1: Pre-open a blank tab synchronously BEFORE any await.
    // IMPORTANT: Do NOT pass 'noopener' here — that causes window.open to return null
    // (per HTML living standard), making the handle-based location assignment impossible.
    const reportTab = window.open('', '_blank');

    // Step 2: If null, popup was blocked — surface an inline error and bail out early.
    if (reportTab === null) {
      setReportError('Please allow pop-ups to view your report.');
      setTimeout(() => setReportError(null), 3000);
      setIsLoadingReport(false);
      return;
    }

    // Step 3: Sever the opener reference immediately, preserving the no-opener security
    // posture of the original 'noopener' feature string without losing the handle.
    reportTab.opener = null;

    try {
      const { reportUrl } = await getReportUrl(session.sessionId);
      // Step 4: Assign the resolved URL to the pre-opened tab.
      // Wrap in try/catch to handle rare cases where the tab was closed by the user.
      try {
        reportTab.location.href = reportUrl;
      } catch {
        reportTab.close();
        setReportError('Failed to open report. Please try again.');
        setTimeout(() => setReportError(null), 3000);
      }
    } catch (error: unknown) {
      // Step 5: Close the blank tab so no stray empty tab is left on fetch failure.
      reportTab.close();
      const message =
        error && typeof error === 'object' && 'message' in error
          ? (error as { message: string }).message
          : 'Failed to load report. Please try again.';
      setReportError(message);
      setTimeout(() => setReportError(null), 3000);
    } finally {
      setIsLoadingReport(false);
    }
  };

  // Determine if session is active
  const isActive = session.status === 'active';

  // TS-007 (T11, SR6, AC-TS007.6b): parent force-ended this session — a genuinely
  // new status branch (no existing cancelled branch to clone here).
  const isEndedByParent = session.status === 'ended_by_parent';

  // Determine badge variant based on status
  const badgeVariant = isActive ? 'default' : 'secondary';
  const badgeText = isActive ? 'Active' : isEndedByParent ? 'Ended early' : 'Completed';

  // Truncate passage for preview
  const passagePreview = truncateText(session.passage || 'No passage', 60);

  // Format timestamp as relative time
  const timestamp = formatRelativeTime(session.updatedAt);

  // Calculate progress (default to 0 if not available)
  // TODO: Derive from actual session data when available
  const progress = 0;

  return (
    <Card
      {...(isActive
        ? {
            onClick: handleClick,
            onKeyDown: handleKeyDown,
            role: 'button' as const,
            tabIndex: 0,
          }
        : {})}
      className={cn(
        'min-h-[60px] rounded-2xl p-4 shadow-sm transition-all',
        isActive
          ? 'cursor-pointer border-orange-200 bg-white hover:-translate-y-0.5 hover:shadow-md'
          : 'border-otter-sand bg-white'
      )}
    >
      <div className="space-y-2">
        {/* Top row: Passage preview, star rating (completed only), and status badge */}
        <div className="flex items-start justify-between gap-3">
          <p
            className={cn(
              'flex-1 text-sm md:text-base font-medium line-clamp-1',
              isActive ? 'text-gray-900' : 'text-gray-500'
            )}
          >
            {passagePreview}
          </p>
          {/* US-018 T4: Show star rating only for non-active (completed/cancelled) sessions.
              DD-B: reads session.starRating only — frontend never recomputes.
              StarRating returns null when starRating is null/undefined (cancelled, active, legacy).
              TS-007 (T11, AC-TS007.6b): NEVER shown for ended_by_parent — no star. */}
          <div className="flex items-center gap-2 shrink-0">
            {!isActive && !isEndedByParent && (
              <StarRating rating={session.starRating} size="sm" />
            )}
            <Badge variant={badgeVariant}>
              {badgeText}
            </Badge>
          </div>
        </div>

        {/* Middle row: Metadata (timestamp) */}
        <p className="text-xs md:text-sm text-gray-500">{timestamp}</p>

        {/* Active session only: Progress bar */}
        {isActive && <ProgressBar value={progress} />}

        {/* Active session only: Continue CTA */}
        {isActive && (
          <Button variant="warm" size="sm" onClick={handleContinueClick} className="w-full">
            Continue
          </Button>
        )}

        {/* Completed session: action buttons — TS-007 (T11): NOT rendered for
            ended_by_parent (no Continue/View Report actions; the card is purely
            informational and non-tappable for a parent-ended session). */}
        {!isActive && !isEndedByParent && (
          <div className="space-y-1">
            <div className="flex gap-2">
              <Button
                variant="warm"
                size="sm"
                onClick={handleClick}
                className="flex-1 h-11"
              >
                View Session
              </Button>
              {session.reportS3Key && (
                <Button
                  variant="warm"
                  size="sm"
                  onClick={handleViewReport}
                  disabled={isLoadingReport}
                  className="flex-1 h-11 bg-transparent border-2 border-otter-orange-500 text-otter-orange-600 hover:bg-otter-orange-50"
                >
                  {isLoadingReport ? 'Loading...' : 'View Report'}
                </Button>
              )}
            </div>
            {reportError && (
              <p className="text-xs text-red-500 text-center">{reportError}</p>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
